/**
 * The ONE place instants become text (docs/00-project-rules/08 §4.1–4.2): the API's JSON
 * (`2026-09-11T06:00:12+07:00`) and the export files (`11/09/2026 06:00:12`). Never hand a `Date`
 * to a file library — they convert through UTC and shift the wall clock by 7 hours silently.
 *
 * Vietnam has had no daylight-saving time since 1975, so the offset is the constant +07:00; the
 * wall-clock parts still come from Intl in the business zone, never from the process zone.
 */
import { BUSINESS_TIME_ZONE } from './timeZone.js';

const PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** @param {Date} instant */
function wallClock(instant) {
  /** @type {Record<string, string>} */
  const parts = {};
  for (const part of PARTS.formatToParts(instant)) parts[part.type] = part.value;
  return parts;
}

/**
 * `2026-09-11T06:00:12+07:00`.
 * @param {Date} instant
 * @returns {string}
 */
export function vnInstant(instant) {
  const p = wallClock(instant);
  return `${p['year']}-${p['month']}-${p['day']}T${p['hour']}:${p['minute']}:${p['second']}+07:00`;
}

/**
 * `11/09/2026 06:00:12` — for export files.
 * @param {Date} instant
 * @returns {string}
 */
export function vnDateTimeText(instant) {
  const p = wallClock(instant);
  return `${p['day']}/${p['month']}/${p['year']} ${p['hour']}:${p['minute']}:${p['second']}`;
}

/**
 * The Vietnam calendar day at instant `now` — `2026-09-12`.
 *
 * Lives here, at the bottom layer, because BOTH ends need the same answer: the scraper decides
 * which day to fetch, and the web app decides which day the global period means. The machine's
 * own day is never the business day (the VPS runs in UTC, where 06:00 in Vietnam is still
 * yesterday).
 *
 * @param {Date} now
 * @returns {string}
 */
export function businessToday(now) {
  const p = wallClock(now);
  return `${p['year']}-${p['month']}-${p['day']}`;
}

/**
 * `2026-09-11` → `11/09/2026` — a calendar day, no instant involved.
 * @param {string} isoDate
 * @returns {string}
 */
export function vnDateText(isoDate) {
  const [y, m, d] = isoDate.split('-');
  return `${d}/${m}/${y}`;
}
